import { Component, Type } from "@wonderlandengine/api";
import { vec3 } from "gl-matrix";

/*** Player movement with Virtual Joystic and W/A/S/D keys */
export class Joystick extends Component {
  static TypeName = "joystick";
  static Properties = {
    /** Movement speed in m/s. */
    speed: { type: Type.Float, default: 1 },
    /** Object of which the orientation is used to determine forward direction */
    NonVrCamera: { type: Type.Object },
    /** parent object */
    Player: { type: Type.Object },
    /** Whether or not to restrict movement on the Y axis */
    restrictY: { type: Type.Bool, default: false },
  };

  start() {
    this.createDynamicHtml();
    setTimeout(this.createJoystick(), 2000); // giving some time for the dynamic html creation
    this.headObject = this.NonVrCamera || this.object;
  }

  update(dt) {
    let direction = [0, 0, 0];

    // movement controls
    if (this.joystick1.x != null) direction[0] += this.joystick1.x;
    if (this.joystick1.y != null) direction[2] += this.joystick1.y;

    // horizontal view controls
    if (this.joystick2.x != null)
      this.Player.rotateAxisAngleDegObject([0, 1, 0], -2 * this.joystick2.x);

    vec3.transformQuat(direction, direction, this.headObject.transformWorld);

    if (this.restrictY) direction[1] = 0; // y restricts
    vec3.normalize(direction, direction); // normalize

    direction[0] *= this.speed * dt;
    direction[2] *= this.speed * dt;
    this.Player.translate(direction);
  }

  createJoystick() {
    class JoystickController {
      // stickID: ID of HTML element (representing joystick) that will be dragged
      // maxDistance: maximum amount joystick can move in any direction
      // deadzone: joystick must move at least this amount from origin to register value change
      constructor(stickID, maxDistance, deadzone) {
        this.id = stickID;
        let stick = document.getElementById(stickID);

        // location from which drag begins, used to calculate offsets
        this.dragStart = null;

        // track touch identifier in case multiple joysticks present
        this.touchId = null;

        this.active = false;
        this.value = { x: 0, y: 0 };

        let self = this;

        function handleDown(event) {
          self.active = true;

          // all drag movements are instantaneous
          stick.style.transition = "0s";

          // touch event fired before mouse event; prevent redundant mouse event from firing
          event.preventDefault();

          if (event.changedTouches)
            self.dragStart = {
              x: event.changedTouches[0].clientX,
              y: event.changedTouches[0].clientY,
            };
          else self.dragStart = { x: event.clientX, y: event.clientY };

          // if this is a touch event, keep track of which one
          if (event.changedTouches)
            self.touchId = event.changedTouches[0].identifier;
        }

        function handleMove(event) {
          if (!self.active) return;

          // if this is a touch event, make sure it is the right one
          // also handle multiple simultaneous touchmove events
          let touchmoveId = null;
          if (event.changedTouches) {
            for (let i = 0; i < event.changedTouches.length; i++) {
              if (self.touchId == event.changedTouches[i].identifier) {
                touchmoveId = i;
                event.clientX = event.changedTouches[i].clientX;
                event.clientY = event.changedTouches[i].clientY;
              }
            }

            if (touchmoveId == null) return;
          }

          const xDiff = event.clientX - self.dragStart.x;
          const yDiff = event.clientY - self.dragStart.y;
          const angle = Math.atan2(yDiff, xDiff);
          const distance = Math.min(maxDistance, Math.hypot(xDiff, yDiff));
          const xPosition = distance * Math.cos(angle);
          const yPosition = distance * Math.sin(angle);

          // move stick image to new position
          stick.style.transform = `translate3d(${xPosition}px, ${yPosition}px, 0px)`;

          // deadzone adjustment
          const distance2 =
            distance < deadzone
              ? 0
              : (maxDistance / (maxDistance - deadzone)) *
                (distance - deadzone);
          const xPosition2 = distance2 * Math.cos(angle);
          const yPosition2 = distance2 * Math.sin(angle);
          const xPercent = parseFloat((xPosition2 / maxDistance).toFixed(4));
          const yPercent = parseFloat((yPosition2 / maxDistance).toFixed(4));

          self.value = { x: xPercent, y: yPercent };
          self.x = xPercent;
          self.y = yPercent;
        }

        function handleUp(event) {
          if (!self.active) return;

          // if this is a touch event, make sure it is the right one
          if (
            event.changedTouches &&
            self.touchId != event.changedTouches[0].identifier
          )
            return;

          // transition the joystick position back to center
          stick.style.transition = ".2s";
          stick.style.transform = `translate3d(0px, 0px, 0px)`;

          // reset everything
          self.value = { x: 0, y: 0 };
          self.x = 0;
          self.y = 0;
          self.touchId = null;
          self.active = false;
        }

        stick.addEventListener("mousedown", handleDown);
        stick.addEventListener("touchstart", handleDown);
        document.addEventListener("mousemove", handleMove, {
          passive: false,
        });
        document.addEventListener("touchmove", handleMove, {
          passive: false,
        });
        document.addEventListener("mouseup", handleUp);
        document.addEventListener("touchend", handleUp);
      }
    }
    this.joystick1 = new JoystickController("stick1", 64, 8);
    this.joystick2 = new JoystickController("stick2", 64, 8);
  }

  createDynamicHtml() {
    //image asset source assignment (change this source to have custom icons )
    const iconJoystickBodySrc = "images/joystick-body.png";
    const iconJoystickBaseSrc = "images/joystick-base.png";
    const iconJoystickBaseHorizontalSrc = "images/joystick-base-horizontal.png";

    const joystickTranslation = document.createElement("div");
    joystickTranslation.id = "joystickTranslation";
    joystickTranslation.setAttribute(
      "style",
      " width: 128px; position: absolute; left:10%; bottom:5%"
    );
    document.body.appendChild(joystickTranslation);

    const joystickBase = document.createElement("img");
    joystickBase.src = iconJoystickBaseSrc;
    joystickBase.draggable = "false";
    joystickBase.setAttribute("style", "opacity:0.7;");
    joystickBase.setAttribute("data-loaded", "false");
    document.getElementById("joystickTranslation").appendChild(joystickBase);

    const stick1 = document.createElement("div");
    stick1.id = "stick1";
    stick1.setAttribute("style", "position: absolute; left:32px; top:32px;");
    document.getElementById("joystickTranslation").appendChild(stick1);

    const joystickBody1 = document.createElement("img");
    joystickBody1.src = iconJoystickBodySrc;
    joystickBody1.draggable = "false";
    joystickBody1.setAttribute("style", "opacity: 0.7;");
    joystickBody1.setAttribute("data-loaded", "false");
    document.getElementById("stick1").appendChild(joystickBody1);

    const joystickRotation = document.createElement("div");
    joystickRotation.id = "joystickRotation";
    joystickRotation.setAttribute(
      "style",
      " width: 128px; position: absolute; right:10%; bottom:5%"
    );
    document.body.appendChild(joystickRotation);

    const joystickBaseHorizontal = document.createElement("img");
    joystickBaseHorizontal.src = iconJoystickBaseHorizontalSrc;
    joystickBaseHorizontal.draggable = "false";
    joystickBaseHorizontal.setAttribute("style", "opacity:0.7;");
    joystickBaseHorizontal.setAttribute("data-loaded", "false");
    document
      .getElementById("joystickRotation")
      .appendChild(joystickBaseHorizontal);

    const stick2 = document.createElement("div");
    stick2.id = "stick2";
    stick2.setAttribute("style", "position: absolute; right :32px; top:32px;");
    document.getElementById("joystickRotation").appendChild(stick2);

    const joystickBody2 = document.createElement("img");
    joystickBody2.src = iconJoystickBodySrc;
    joystickBody2.draggable = "false";
    joystickBody2.setAttribute("style", "opacity: 0.7;");
    joystickBody2.setAttribute("data-loaded", "false");
    document.getElementById("stick2").appendChild(joystickBody2);
  }
}
